import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { defaultTo } from 'lodash-es';
import { Subject, takeUntil } from 'rxjs';

import {
  User,
  ListProperty,
  ListExpirySubscription,
  NotificationPeriods,
  NotificationDescriptions,
} from '@site-mate/dashpivot-shared-library';

@Component({
  templateUrl: './list-expiry-notification-management.component.html',
  selector: 'cc-list-expiry-notification-management',
  styleUrls: ['./list-expiry-notification-management.component.scss'],
})
export class ListExpiryNotificationManagementComponent implements OnInit, OnDestroy {
  readonly notificationPeriods = NotificationPeriods;
  readonly notificationDescriptions = NotificationDescriptions;
  readonly uniqueIdentifier = Date.now();
  @Input() menuClosed: EventEmitter<void>;
  @Input() property = {} as ListProperty;
  @Input() users = [] as User[];
  @Output() readonly expirySubscription = new EventEmitter<ListExpirySubscription>();
  form: UntypedFormGroup;
  readonly keepDefaultOrder = () => 1;

  private readonly unsubscribe = new Subject<void>();
  private initialValues: any;

  constructor(private readonly formBuilder: UntypedFormBuilder) {}

  ngOnInit() {
    const subscribedUsers = defaultTo(this.property.subscribedUsers, {}) as ListProperty['subscribedUsers'];
    const userIds = defaultTo(subscribedUsers.userIds, []) as typeof subscribedUsers.userIds;
    this.form = this.formBuilder.group({
      userIds: [],
      expiryTimes: this.formBuilder.group({}),
    });
    const notificationPeriods = defaultTo(
      subscribedUsers.notificationPeriods,
      [],
    ) as typeof subscribedUsers.notificationPeriods;
    const periodsMap = Object.keys(this.notificationPeriods).reduce(
      (carry, definition: NotificationPeriods) => {
        carry[definition] = notificationPeriods.includes(definition);
        return carry;
      },
      {} as { [key: string]: any },
    );
    this.form.setControl('expiryTimes', this.formBuilder.group(periodsMap));
    this.form.patchValue({ userIds });
    this.initialValues = this.form.value;
    this.menuClosed.pipe(takeUntil(this.unsubscribe)).subscribe(() => {
      this.form.reset(this.initialValues);
    });
  }

  ngOnDestroy(): void {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  onOpen() {
    if (this.form.value.userIds.length === 0) {
      const expiryTimes = Object.keys(this.notificationPeriods).reduce((carry, item) => {
        carry[item] = false;
        return carry;
      }, {});
      this.form.patchValue({ expiryTimes });
    }
  }

  onSubmit() {
    const rawData = this.form.value;
    const expirySubscription: ListExpirySubscription = {
      userIds: rawData.userIds,
      propertyId: this.property.id,
      notificationPeriods: Object.keys(rawData.expiryTimes)
        .filter((time) => rawData.userIds.length > 0 && rawData.expiryTimes[time])
        .map((entry) => NotificationPeriods[entry]),
    };
    this.expirySubscription.emit(expirySubscription);
  }

  resetForm() {
    const emptyExpiryTimes = {};
    [...Object.keys(this.form.value.expiryTimes)].forEach((expiryTime) => {
      emptyExpiryTimes[expiryTime] = false;
    });
    this.form.patchValue({
      userIds: [],
      expiryTimes: emptyExpiryTimes,
    });
    this.form.markAsDirty();
  }
}
